import React, { useMemo, useEffect } from 'react'
import MerchantGenericCard from '@nexidigital/merchantgenericcard'
import MerchantModal from '@nexidigital/merchantmodal'
import PortalSelect from '@nexidigital/portalselect'
import PropTypes from 'prop-types'
import './PciNeedHelpModal.scss'
import PortalCheckBox from '@nexidigital/portalcheckbox'
import formattedMessage from './languageModule'
import { useIntl } from 'react-intl'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import moment from 'moment'
import { Capacitor } from '@capacitor/core'
import { setFormInteraction } from 'Store/middleware/analytics'
import componentConfig from './componentConfig'

const PciNeedHelpModal = (props) => {
  const intl = useIntl()
  const FORM_ID = 'pci-need-help-modal'

  const isPciPrivacyInfoVisible = componentConfig.pciPrivacyInfo.isVisible()

  // Opzioni select
  const TIME_SLOT_KEY = 'timeSlot'
  const CONSENT_KEY = 'consent'

  const selectOptions = [
    { value: 'AM', label: intl.formatMessage({ id: 't.app.pciNeedHelpModal.requestSlotAM' }) },
    { value: 'PM', label: intl.formatMessage({ id: 't.app.pciNeedHelpModal.requestSlotPM' }) }
  ]

  useEffect(() => {
    handleFormInteractionAnalytic('impression')
  }, [])

  const initialValues = useMemo(
    () => ({
      [TIME_SLOT_KEY]: '',
      [CONSENT_KEY]: false
    }),
    []
  )

  const validationSchema = Yup.object().shape({
    [TIME_SLOT_KEY]: Yup.string().required(formattedMessage.emptyErrorLabel),
    [CONSENT_KEY]: Yup.boolean().oneOf([true], intl.formatMessage({ id: 't.app.error.requiredField' }))
  })

  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnMount: true,
    onSubmit: (values) => {
      const { vatNumber, fiscalCode } = props.merchantData
      const partitaIva = (vatNumber && vatNumber !== 'NO_PIVA') ? vatNumber : (fiscalCode || '')
      const data = {
        form_id: FORM_ID,
        partita_iva: partitaIva,
        id_user: props.userData.id,
        request_slot: values[TIME_SLOT_KEY],
        contact_name: `${props.userData.firstName} ${props.userData.lastName}`,
        phone_contact: `${props.userData.prefix}${props.userData.telephoneNumber}`,
        contact_origination: Capacitor.isNativePlatform ? 'WEB-DESKTOP' : 'APP-MOBILE',
        rag_sociale: props.merchantData.companyName,
        request_date: moment(new Date()).format('YYYY-MM-DD'),
        call_motivation: 'PCI support request'
      }
      props.actions.doNeedSupport(data)
      props.actions.doHidePopup()
    }
  })

  const handleFormInteractionAnalytic = (interactionType, element) => {
    setFormInteraction(interactionType, FORM_ID, element)
  }

  const handleSubmit = () => {
    // Se ci sono errori di validazione, traccia gli errori prima del submit
    if (!formik.isValid) {
      Object.keys(formik.errors).forEach(fieldKey => {
        if (formik.errors[fieldKey]) {
          handleFormInteractionAnalytic('input error', fieldKey)
        }
      })
    }
    formik.handleSubmit()
  }

  return (
    <MerchantModal
      onClose={() => props.actions.doHidePopup()}
      topBarTitle={formattedMessage.topBarTitle}
      primaryButtonLabel={formattedMessage.primaryButtonLabel}
      primaryAction={handleSubmit}
      isPrimaryLoading={formik.isSubmitting}
      isButtonDisabled={!formik.isValid || formik.isSubmitting}
    >
      <MerchantGenericCard title={formattedMessage.cardTitle} subtitle={formattedMessage.cardSubtitle}>
        <form id={FORM_ID}>
          <div className='pciCallBackModalContent'>
            <div className='pciCallBackModalHours'>
              <PortalSelect
                id={TIME_SLOT_KEY}
                name={TIME_SLOT_KEY}
                options={selectOptions}
                label={formattedMessage.selectLabel}
                placeholder={intl.formatMessage({ id: 't.app.pciNeedHelpModal.selectPlaceholder' })}
                value={formik.values[TIME_SLOT_KEY]}
                onChange={(e) => {
                  formik.setFieldValue(TIME_SLOT_KEY, e.target ? e.target.value : e.value)
                }}
                onBlur={() => handleFormInteractionAnalytic('focus', TIME_SLOT_KEY)}
                error={formik.touched[TIME_SLOT_KEY] && Boolean(formik.errors[TIME_SLOT_KEY])}
                errorText={formik.touched[TIME_SLOT_KEY] && formik.errors[TIME_SLOT_KEY]}
              />
            </div>
            <div className='pciCallBackModalPrivacy'>
              <div className='pciCallBackModalPrivacyContent'>
                <span className='pciCallBackModalPrivacyTitle'>{formattedMessage.privacyTitle}</span>
                <p className='pciCallBackModalPrivacyText'>{formattedMessage.privacyText}</p>
              </div>
              {isPciPrivacyInfoVisible && <p className='pciCallBackModalPrivacyInfo'>{formattedMessage.privacyInfo}</p>}
              <PortalCheckBox
                name={CONSENT_KEY}
                onChange={({ id, checked }) => formik.setFieldValue(CONSENT_KEY, checked)}
                options={[
                  {
                    id: CONSENT_KEY,
                    label: formattedMessage.checkboxLabel,
                    value: formik.values[CONSENT_KEY],
                    checked: formik.values[CONSENT_KEY],
                    error: Boolean(formik.errors[CONSENT_KEY] && formik.touched[CONSENT_KEY]),
                    errorMessage: formik.touched[CONSENT_KEY] && formik.errors[CONSENT_KEY] ? formik.errors[CONSENT_KEY] : undefined
                  }
                ]}
                centered
              />
              <p className='pciCallBackModalPrivacyInfo'>{formattedMessage.callbackInfo(`${props.userData.prefix} ${props.userData.telephoneNumber}`)}</p>
            </div>
          </div>
        </form>
      </MerchantGenericCard>
    </MerchantModal>
  )
}

PciNeedHelpModal.propTypes = {
  actions: PropTypes.object,
  merchantData: PropTypes.object,
  userData: PropTypes.object,
  needSupport: PropTypes.object
}

PciNeedHelpModal.defaultProps = {
  actions: {},
  merchantData: {},
  userData: {},
  needSupport: {}
}

export default PciNeedHelpModal
